import { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { trackZhFunnel } from "@/lib/zhFunnel";

declare global {
  interface Window {
    google?: any;
    googleTranslateElementInit?: () => void;
  }
}

// Quick-access languages for our most likely international audiences.
const LANGUAGES = [
  { code: "", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  { code: "zh-CN", label: "中文 (Mandarin)", flag: "🇨🇳" },
];

// Maps a code to a clean analytics label (e.g. "Language Selected: Spanish").
const ANALYTICS_LABELS: Record<string, string> = {
  "": "English",
  es: "Spanish",
  fr: "French",
  nl: "Dutch",
  "zh-CN": "Mandarin",
};

const COOKIE = "googtrans";

function setGoogleTransCookie(lang: string) {
  const value = lang ? `/en/${lang}` : "";
  const domains = [window.location.hostname, "." + window.location.hostname];
  // Clear then set across path/domain variants so the widget picks it up
  const expire = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `${COOKIE}=; ${expire}; path=/`;
  domains.forEach((d) => {
    document.cookie = `${COOKIE}=; ${expire}; path=/; domain=${d}`;
  });
  if (value) {
    document.cookie = `${COOKIE}=${value}; path=/`;
    domains.forEach((d) => {
      document.cookie = `${COOKIE}=${value}; path=/; domain=${d}`;
    });
  }
}

function getCurrentLang(): string {
  const match = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

const LanguageSelector = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrent(getCurrentLang());

    // Inject the hidden Google Translate element container
    if (!document.getElementById("google_translate_element")) {
      const div = document.createElement("div");
      div.id = "google_translate_element";
      div.style.display = "none";
      document.body.appendChild(div);
    }

    window.googleTranslateElementInit = () => {
      if (window.google?.translate) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            autoDisplay: false,
          },
          "google_translate_element",
        );
      }
    };

    if (!document.getElementById("google-translate-script")) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    } else if (window.google?.translate) {
      window.googleTranslateElementInit();
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectLanguage = async (code: string) => {
    const langName = ANALYTICS_LABELS[code] ?? code ?? "English";

    // Fire analytics event (e.g. "Language Selected: Spanish")
    trackEvent("Language Selected", { language: langName });

    // Persist for the insight dashboard (best-effort, never block the UI)
    try {
      await supabase.from("language_selections").insert({
        language_code: code || "en",
        language_label: langName,
        page_path: window.location.pathname,
      });
    } catch (err) {
      console.warn("Language log error:", err);
    }

    // Mandarin funnel: a Google Translate switch into 中文 is the top of the funnel
    if (code.toLowerCase().startsWith("zh")) {
      void trackZhFunnel("Mandarin Page View", {
        source: "google_translate",
        pagePath: window.location.pathname,
        languageCode: code,
      });
    }

    setGoogleTransCookie(code);
    setCurrent(code);
    setOpen(false);
    window.location.reload();
  };

  const active = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <div ref={ref} className="relative notranslate">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 p-2 text-foreground/70 hover:text-primary transition-colors"
        aria-label="Select language"
      >
        <Globe className="w-5 h-5" />
        <span className="hidden sm:inline text-sm font-sans">{active.flag}</span>
        <ChevronDown aria-hidden className={`hidden min-[360px]:block w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 w-44 bg-background border border-border rounded-md shadow-lg py-2 z-50 animate-fade-in-up">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code || "en"}
              onClick={() => selectLanguage(lang.code)}
              className={`flex items-center gap-2.5 w-full px-4 py-2 text-sm font-sans text-left transition-colors hover:bg-secondary/50 ${
                active.code === lang.code ? "text-primary font-medium" : "text-foreground/80"
              }`}
            >
              <span>{lang.flag}</span>
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
